import { useState, ReactNode } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  ExternalLink,
  Plus,
  Edit,
  Trash2,
  Search,
  Star,
  Clock,
  TrendingUp,
  FileText,
  Video,
  Calculator,
  BookOpen,
  MessageCircle,
  Settings,
  Calendar,
  Link,
} from 'lucide-react';
import PageHeader from '../../components/common/PageHeader';

interface QuickLink {
  id: string;
  title: string;
  url: string;
  description: string;
  category: string;
  isFavorite: boolean;
  lastAccessed: string;
  icon: ReactNode;
}

const QuickLinks = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const categories = [
    { id: 'all', name: 'All Links', icon: Link },
    { id: 'trading', name: 'Trading Tools', icon: TrendingUp },
    { id: 'education', name: 'Education', icon: BookOpen },
    { id: 'resources', name: 'Resources', icon: FileText },
    { id: 'support', name: 'Support', icon: MessageCircle },
  ];

  const quickLinks: QuickLink[] = [
    {
      id: '1',
      title: 'Economic Calendar',
      url: 'https://www.forexfactory.com/calendar',
      description: 'Track important economic events and news releases',
      category: 'trading',
      isFavorite: true,
      lastAccessed: '2 hours ago',
      icon: <Calendar className="h-4 w-4" />,
    },
    {
      id: '2',
      title: 'Trading Calculator',
      url: '#',
      description: 'Calculate profits, pips, and position sizes',
      category: 'trading',
      isFavorite: true,
      lastAccessed: '1 day ago',
      icon: <Calculator className="h-4 w-4" />,
    },
    {
      id: '3',
      title: 'Video Tutorials',
      url: '#',
      description: 'Learn trading strategies and platform features',
      category: 'education',
      isFavorite: false,
      lastAccessed: '3 days ago',
      icon: <Video className="h-4 w-4" />,
    },
    {
      id: '4',
      title: 'Market Analysis',
      url: '#',
      description: 'Daily market analysis and trading signals',
      category: 'resources',
      isFavorite: true,
      lastAccessed: '5 hours ago',
      icon: <TrendingUp className="h-4 w-4" />,
    },
    {
      id: '5',
      title: 'API Documentation',
      url: '#',
      description: 'Integrate with our trading API',
      category: 'resources',
      isFavorite: false,
      lastAccessed: '1 week ago',
      icon: <FileText className="h-4 w-4" />,
    },
    {
      id: '6',
      title: 'Live Support Chat',
      url: '/supports',
      description: 'Get instant help from our support team',
      category: 'support',
      isFavorite: false,
      lastAccessed: '30 minutes ago',
      icon: <MessageCircle className="h-4 w-4" />,
    },
  ];

  const filteredLinks = quickLinks.filter(link => {
    const matchesSearch =
      link.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      link.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory =
      selectedCategory === 'all' || link.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const favoriteLinks = quickLinks.filter(link => link.isFavorite);
  const recentlyUsed = quickLinks
    .sort((a, b) => {
      const timeA = a.lastAccessed.includes('hour')
        ? 0
        : a.lastAccessed.includes('minute')
          ? -1
          : 1;
      const timeB = b.lastAccessed.includes('hour')
        ? 0
        : b.lastAccessed.includes('minute')
          ? -1
          : 1;
      return timeA - timeB;
    })
    .slice(0, 3);

  return (
    <div className="space-y-6">
      <div className="sticky top-0 z-20 bg-background pt-4">
        <PageHeader
          title="Quick Links"
          description="Access your favorite trading tools and resources"
          actionLabel="Add Link"
          actionIcon={Plus}
          onAction={() => console.log('Add link')}
        />

        <div className="py-2 flex flex-col md:flex-row gap-4 items-center justify-between">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              placeholder="Search links..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {categories.map(category => {
              const Icon = category.icon;
              return (
                <Button
                  key={category.id}
                  variant={
                    selectedCategory === category.id ? 'default' : 'outline'
                  }
                  size="sm"
                  onClick={() => setSelectedCategory(category.id)}
                  className="flex items-center gap-2"
                >
                  <Icon className="h-4 w-4" />
                  {category.name}
                </Button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Star className="h-5 w-5 text-yellow-500" />
                Favorite Links
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {favoriteLinks.map(link => (
                  <div
                    key={link.id}
                    className="p-2 border rounded-lg hover:shadow-md transition-shadow cursor-pointer group"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-100 rounded-lg text-blue-600 group-hover:bg-blue-200 transition-colors">
                          {link.icon}
                        </div>
                        <div className="flex-1">
                          <h3 className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors">
                            {link.title}
                          </h3>
                          <p className="text-sm text-gray-500 mt-1">
                            {link.description}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-3">
                      <Badge variant="secondary" className="text-xs">
                        {categories.find(c => c.id === link.category)?.name}
                      </Badge>
                      <div className="flex items-center gap-1 text-xs text-gray-500">
                        <Clock className="h-3 w-3" />
                        {link.lastAccessed}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>All Links</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {filteredLinks.map(link => (
                  <div
                    key={link.id}
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 transition-colors group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-gray-100 rounded-lg text-gray-600 group-hover:bg-gray-200 transition-colors">
                        {link.icon}
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900 flex items-center gap-2">
                          {link.title}
                          {link.isFavorite && (
                            <Star className="h-3 w-3 text-yellow-500 fill-current" />
                          )}
                        </h3>
                        <p className="text-sm text-gray-500">
                          {link.description}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">
                        {categories.find(c => c.id === link.category)?.name}
                      </Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <a
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center h-8 w-8 p-0 rounded-md hover:bg-gray-100 transition-colors"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Recently Used
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div>
                {recentlyUsed.map(link => (
                  <a
                    key={link.id}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="p-1.5 bg-gray-100 rounded text-gray-600">
                      {link.icon}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">
                        {link.title}
                      </p>
                      <p className="text-xs text-gray-500">
                        {link.lastAccessed}
                      </p>
                    </div>
                    <ExternalLink className="h-3 w-3 text-gray-400" />
                  </a>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button variant="outline" className="w-full justify-start">
                <Settings className="h-4 w-4 mr-2" />
                Manage Categories
              </Button>
              <Button variant="outline" className="w-full justify-start">
                <Star className="h-4 w-4 mr-2" />
                View All Favorites
              </Button>
              <Button variant="outline" className="w-full justify-start">
                <ExternalLink className="h-4 w-4 mr-2" />
                Import Bookmarks
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Statistics</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Total Links</span>
                <span className="font-semibold">{quickLinks.length}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Favorites</span>
                <span className="font-semibold">{favoriteLinks.length}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Categories</span>
                <span className="font-semibold">{categories.length - 1}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default QuickLinks;
